import {StrictMode} from 'react'
import * as Sentry from '@sentry/react'
import type {QueryKey} from '@tanstack/react-query'
import {
  MutationCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query'
import {ReactQueryDevtools} from '@tanstack/react-query-devtools'
import {createRoot} from 'react-dom/client'
import {Provider as ReduxProvider} from 'react-redux'
import {createBrowserRouter, RouterProvider} from 'react-router'
import {rootRouter} from 'app/router'
import {Prompt} from 'shared/Prompt'
import {Toaster, toast} from 'shared/toaster'
import {store} from 'store/store'
import 'styles/index.css'

if (import.meta.env.VITE_SENTRY_URL) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_URL,
    environment: import.meta.env.DEV
      ? 'development'
      : window.location.hostname === 'whnue.com'
        ? 'production'
        : 'staging',
    maxBreadcrumbs: 50,
    ignoreErrors: [
      'ResizeObserver',
      'POST /authentication/jwt/refresh/ [400]',
      'POST /authentication/jwt/token/ [400]',
    ],
    // We recommend adjusting this value in production, or using tracesSampler
    // for finer control
    tracesSampleRate: 1.0,
    // This sets the sample rate to be 10%. You may want this to be 100% while
    // in development and sample at a lower rate in production
    replaysSessionSampleRate: 0.1,

    // If the entire session is not sampled, use the below sample rate to sample
    // sessions when an error occurs.
    replaysOnErrorSampleRate: 1.0,
  })
}

declare module '@tanstack/react-query' {
  interface Register {
    mutationMeta: {
      invalidatesQuery?: QueryKey
      invalidatesQueries?: QueryKey[]
      successMessage?: string
      errorMessage?: string
    }
  }
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
  mutationCache: new MutationCache({
    onSuccess: (_data, _variables, _context, mutation) => {
      if (mutation.meta?.successMessage) {
        toast.success(mutation.meta.successMessage)
      }
    },

    onError: (error, _variables, _context, mutation) => {
      if (mutation.meta?.errorMessage) {
        toast.error(mutation.meta.errorMessage, {description: error.message})
      }
    },
    onSettled: (_data, _error, _variables, _context, mutation) => {
      {
        if (mutation.meta?.invalidatesQuery) {
          queryClient.invalidateQueries({
            queryKey: mutation.meta?.invalidatesQuery,
          })
        }
        if (mutation.meta?.invalidatesQueries) {
          Promise.all(
            mutation.meta?.invalidatesQueries.map((queryKey) =>
              queryClient.invalidateQueries({
                queryKey,
              })
            )
          )
        }
      }
    },
  }),
})

const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouterV7(createBrowserRouter)

const router = sentryCreateBrowserRouter(rootRouter)

// https://stackoverflow.com/a/72139826
let container: HTMLElement | null = null

document.addEventListener('DOMContentLoaded', function () {
  if (!container) {
    container = document.getElementById('root') as HTMLElement
    const root = createRoot(container)
    root.render(
      <StrictMode>
        <QueryClientProvider client={queryClient}>
          <ReduxProvider store={store}>
            <RouterProvider router={router} />
            <Toaster />
            <Prompt />
            <ReactQueryDevtools initialIsOpen={false} position='bottom' />
          </ReduxProvider>
        </QueryClientProvider>
      </StrictMode>
    )
  }
})
